import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

// Accepts anything Supabase (ISO string), Firebase JS SDK Timestamp (has
// .toDate()), Firebase Admin SDK Timestamp (bare { _seconds, _nanoseconds }
// / { seconds, nanoseconds }), Date, or number. Returns '' for unparseable
// input so callers don't have to guard. This matters because the advertiser
// mirror doc is written by the Admin SDK (Timestamp) but the wallet state
// comes from PostgREST (ISO string) — both land in the same UI.
type TimestampLike =
  | Date
  | string
  | number
  | { toDate: () => Date }
  | { seconds: number; nanoseconds?: number }
  | { _seconds: number; _nanoseconds?: number };

function coerceDate(value: TimestampLike | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof (value as { seconds?: unknown }).seconds === 'number') {
    return new Date((value as { seconds: number }).seconds * 1000);
  }
  if (typeof (value as { _seconds?: unknown })._seconds === 'number') {
    return new Date((value as { _seconds: number })._seconds * 1000);
  }
  return null;
}

export function formatDate(value: TimestampLike | null | undefined): string {
  const d = coerceDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

export function formatDateTime(value: TimestampLike | null | undefined): string {
  const d = coerceDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}
